<script setup>
import { computed, useAttrs } from 'vue'

const SIZE_CLASSES = {
    small: 'base-button-small',
    medium: 'base-button-medium',
    xsmall: 'base-button-xsmall',
    xlarge: 'base-button-xlarge',
    iconOnly: 'base-button-icon-only',
    hero: 'base-button-hero',
}

const TYPE_CLASSES = {
    primary: 'base-button-primary',
    primaryFlat: 'base-button-primary-flat',
    secondary: 'base-button-secondary',
    secondaryPink: 'base-button-secondary-pink',
    highlight: 'base-button-highlight',
    nadir: 'base-button-nadir',
    blue: 'base-button-blue',
    facebook: 'base-button-facebook',
    google: 'base-button-google',
    apple: 'base-button-apple',
    tag: 'base-button-tag',
    tagSecondary: 'base-button-tag-secondary',
    tagBlack: 'base-button-tag-black',
    black: 'base-button-black',
    white: 'base-button-white',
    follow: 'base-button-follow',
    transparent: 'base-button-transparent',
    transparentWhite: 'base-button-transparent-white',
    dots: 'base-button-dots',
    cross: 'base-button-cross',
}

const MODIFIER_CLASSES = {
    dots: 'base-button-dots',
    fill: 'base-button-fill',
    autoWidth: 'base-button-auto-width',
    noMargins: 'base-button-no-margins',
    roundCorners: 'base-button-round-corners',
}

const props = defineProps({
    icon: {
        type: [String, Boolean],
        default: '',
    },
    size: {
        type: String,
        default: 'large',
    },
    buttonType: {
        type: String,
        default: 'primary', // tag, primary, primaryFlat, secondary, transparent, tagSecondary, facebook, highlight, tagBlack, black, nadir, blue, follow, secondaryPink
    },
    active: {
        type: Boolean,
        default: false,
    },
    modifiers: {
        type: Object,
        default: () => ({
            dots: false,
            autoWidth: false,
            fill: false,
            noMargins: false,
            roundCorners: false,
        }),
    },
})

const attrs = useAttrs()

const isLabel = computed(() => !attrs.onClick && attrs.type !== 'submit')

const sizeClass = computed(() => SIZE_CLASSES[props.size])

const typeClass = computed(() => TYPE_CLASSES[props.buttonType])

const modifierClasses = computed(() =>
    Object.keys(MODIFIER_CLASSES)
        .filter(name => props.modifiers[name])
        .map(name => MODIFIER_CLASSES[name])
)

const iconClasses = computed(() => [
    'base-button-icon',
    props.icon === 'signal' && 'base-button-icon-signal',
    props.size === 'iconOnly' && 'base-button-icon-only',
])
</script>
<template>
    <button
        :class="[
            'base-button',
            sizeClass,
            typeClass,
            modifierClasses,
            isLabel && 'base-button-cannot-be-hovered',
            active && 'base-button-active',
        ]"
    >
        <span class="base-button-helper" />
        <BaseIcon
            v-if="icon"
            :name="icon"
            :class="iconClasses"
        />
        <span
            v-if="size !== 'icon'"
            class="base-button-main"
        >
            <slot />
        </span>
    </button>
</template>

<style lang="scss" scoped>
@use 'sass:color';
@use '@design' as *;

// The flat hover/press pair. Pair it with `@include transition(background-color)`
// on the same rule — that base transition stays at the call site so it keeps its
// place among the rule's own declarations.
@mixin flatStates($hover, $pressed) {
    &:not(:disabled):hover {
        background: $hover;
    }

    &:active,
    &.base-button-active {
        background: $pressed;

        @include transition(background-color, fast, exit);
    }
}

@mixin brandedButton($color) {
    position: relative;
    padding: 0 $s-xxl;
    font-weight: 600;
    color: $c-text-on-accent;
    word-spacing: 2px;
    letter-spacing: 1.5px;
    background: $color;

    @include transition(background-color);
    @include flatStates(color.adjust($color, $lightness: -6%), color.adjust($color, $lightness: -12%));
}

// Top-level selectors stay wrapped in `:where()` so the scoping attribute does
// not raise their specificity above the overrides callers pass in via `:class`.
// See "Component authoring standard > Styles" in the README.
:where(.base-button-icon) {
    position: absolute;
    top: 50%;
    left: $s-m;
    padding-right: $s-xxs;
    margin-top: -0.6rem;
    color: $c-white;

    @include square(1.2rem);
}

:where(.base-button-icon-only) {
    @include center;

    margin-top: 0;
}

:where(.base-button-icon-signal) {
    left: 1.5rem;
    margin-top: -0.6rem;

    @include square(1.2rem);

    + .base-button-main {
        padding-right: $s-xxl;
        padding-left: $s-xxl + $s-m;
    }
}

:where(.base-button-small .base-button-icon) {
    left: 0.5em;

    + .base-button-main {
        padding-right: 0;
        padding-left: 0;
        text-indent: 0.8rem;
    }
}

:where(.base-button-medium .base-button-icon) {
    left: 0.5em;

    + .base-button-main {
        padding-right: 0;
        padding-left: 0;
        text-indent: 1.1rem;
    }
}

:global(.base-button) {
    margin-bottom: $button-margin;
    font-size: 14px;
}

:where(.base-button) {
    // Base button styles
    position: relative;
    z-index: $layer-page-z-index;
    width: 100%;
    height: $size-touch-target;
    padding: $size-button-padding;
    font-weight: 600;
    cursor: pointer;
    background: none;
    border: 0;
    border-radius: $button-border-radius;

    &::before,
    &::after {
        border-radius: $button-border-radius;
    }

    @include desktop {
        width: auto;
        min-width: 160px;
        height: $s-xxl;
    }

    &.base-button-small,
    &.base-button-medium {
        display: flex;
        align-items: center;
        justify-content: center;
        width: auto;
        min-width: 76px;
        height: $s-xl;
        padding: 0 $s-s;
        margin-bottom: 0;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
    }

    &.base-button-medium {
        font-size: 12px;
    }

    &.base-button-xsmall {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 18px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
    }

    &.base-button-xlarge {
        height: auto;
        padding: $s-l;
    }

    &.base-button-icon-only {
        min-width: 0;
    }

    // Button type styles

    // Flat accent (Colors §02): hover DARKENS so on-accent text stays AA; press
    // crossfades to accent/pressed within motion/fast. Shape unchanged until T8.
    &.base-button-primary,
    &.base-button-primary-flat,
    &.base-button-highlight {
        color: $c-text-on-accent;
        letter-spacing: 1px;
        background: $c-accent-default;
        border: none;

        @include transition(background-color);
        @include flatStates($c-accent-hover, $c-accent-pressed);
    }

    // primary-flat call sites (modals) pad vertically against the fixed button
    // height and rely on flex centering to keep the label centered.
    &.base-button-primary-flat {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &.base-button-secondary {
        color: $c-button-text;
        letter-spacing: 1px;
        background-clip: padding-box;
        border-color: transparent;

        @include transition(background-color);

        &::before {
            z-index: $layer-negative-z-index;
            content: '';
            border: 3px solid $c-accent-default;

            @include fill;
        }

        @include flatStates($c-state-hover, $c-state-pressed);
    }

    &.base-button-nadir {
        color: $c-button-text;
        border: none;

        &::before {
            z-index: $layer-negative-z-index;
            content: '';
            background: $c-text-tertiary;

            @include fill;
        }

        &:hover {
            &::before {
                background: $c-text-secondary;
            }
        }
    }

    &.base-button-blue {
        @include brandedButton($c-channel-chill);
    }

    &.base-button-facebook {
        @include brandedButton($c-facebook);
    }

    &.base-button-google {
        @include brandedButton($c-google);
    }

    &.base-button-apple {
        @include brandedButton($c-apple-grey);
    }

    &.base-button-tag {
        color: $c-white;

        &::after {
            z-index: $layer-negative-z-index;
            content: '';
            background-color: $c-text-tertiary;
            border-radius: $radius-3;
            opacity: 0.1;

            @include fill;
        }

        &:hover::after {
            opacity: 0.4;
        }

        &:active::after,
        &.base-button-active::after {
            background-color: $c-highlight;
            opacity: 1;
        }
    }

    &.base-button-tag-secondary {
        z-index: $layer-page-z-index;
        color: $c-white;
        background: none;

        @include transition(background-color);

        &::before {
            z-index: $layer-negative-z-index;
            box-sizing: border-box;
            content: '';
            border: 2px solid $c-surface-chrome;

            @include fill;
        }

        @include flatStates($c-state-hover, $c-state-pressed);
    }

    &.base-button-black {
        z-index: $layer-page-z-index;
        color: $c-white;

        &::before {
            z-index: $layer-negative-z-index;
            box-sizing: border-box;
            content: '';
            background: $c-black;

            @include fill;
        }

        &:not(:disabled):hover::before {
            background: linear-gradient($c-state-hover, $c-state-hover), $c-black;
        }
    }

    &.base-button-white {
        z-index: $layer-page-z-index;
        color: $c-black;

        &::before {
            z-index: $layer-negative-z-index;
            box-sizing: border-box;
            content: '';
            background: $c-white;

            @include transition(background-color);
            @include fill;
        }

        &:not(:disabled):hover::before {
            background: $c-text-secondary;
        }
    }

    &.base-button-tag-black {
        z-index: $layer-page-z-index;
        color: $c-white;

        @include transition(background-color);

        &::before {
            z-index: $layer-negative-z-index;
            box-sizing: border-box;
            content: '';
            border: 2px solid $c-text-disabled;

            @include fill;
        }

        @include flatStates($c-state-hover, $c-state-pressed);
    }

    &.base-button-follow {
        font-weight: 600;
        color: $c-white;
        text-transform: none;
        letter-spacing: 0.5px;
        background: transparent;
        border: 2px solid $c-white;
        border-radius: $radius-16;

        @include transition((color, background-color, border-color, opacity));

        &::before,
        &::after {
            display: none;
        }

        &:hover {
            opacity: 0.8;
        }

        &.base-button-active {
            color: $c-accent-default;
            border-color: $c-accent-default;
        }
    }

    &.base-button-secondary-pink {
        font-weight: 600;
        color: $c-white;
        text-transform: none;
        letter-spacing: 0.5px;
        background: $c-accent-default;
        border: none;
        border-radius: $radius-8;

        @include transition((color, background-color, border-color));

        &::before,
        &::after {
            display: none;
        }

        &:hover {
            background: $c-accent-hover;
        }

        &:active,
        &.base-button-active {
            background: $c-accent-pressed;
        }
    }

    // Button modifiers

    &.base-button-auto-width {
        width: auto;
        min-width: unset;
    }

    &.base-button-fill {
        width: 100%;
    }

    &.base-button-transparent {
        font-weight: normal;
        color: $c-text-faded;
        text-transform: none;
        background: none;

        &-white {
            color: $c-white;
            text-transform: uppercase;

            @include transition(background-color);
            &:hover {
                background: $c-highlight;
            }
        }

        &::before {
            background: none;
        }
        &::after {
            background: none;
        }

        &:hover {
            color: $c-highlight;
            .base-button-icon {
                color: $c-highlight;
            }
        }
    }

    &.base-button-dots {
        min-width: 28px;
        color: $c-white;
        .base-button-helper {
            display: block;
            background: currentColor;
            border-radius: 50%;

            @include square(4px);
            @include center;

            &::before,
            &::after {
                position: absolute;
                top: 0;
                display: block;
                content: '';
                background: currentColor;
                border-radius: 50%;

                @include square(4px);
            }

            &::before {
                left: -7px;
            }

            &::after {
                left: 7px;
            }
        }
    }

    &.base-button-cross {
        min-width: 28px;
        .base-button-helper {
            display: block;

            &::before,
            &::after {
                display: block;
                width: 9px;
                height: 2px;
                content: '';
                background-color: $c-white;
                transform-origin: center;

                @include center;
            }

            &::after {
                transform: translate(-50%, -50%) rotate(90deg);
            }
        }
    }

    &.base-button-cannot-be-hovered {
        pointer-events: none;
        cursor: default;
    }

    &.base-button-no-margins {
        margin: 0;
    }

    &.base-button-round-corners {
        overflow: hidden;
        border-radius: $size-radius-l;
    }

    // Brand CTA (Figma 816:370): taller, roomier and more rounded than the
    // default button.
    // Deliberately declared last. `size` and `buttonType` are orthogonal props
    // but compile to sibling compound selectors of equal specificity
    // (`.base-button.base-button-hero` vs `.base-button.base-button-primary-flat`),
    // so source order decides. Every type rule above sets `font-weight`, and
    // `.base-button-transparent` and `.base-button-round-corners` set `font-weight`
    // and `border-radius` — declaring `hero` here is what lets it win without a
    // specificity hack at the call site.
    &.base-button-hero {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 52px;
        padding: 0 $s-xxl;
        font-size: 18px;
        font-weight: 700;
        white-space: nowrap;
        border-radius: $size-radius-m;
    }

    &:disabled {
        cursor: not-allowed;
        background: $c-button-disabled-bg;
        border: 1px solid $c-surface-overlay;
        &::before,
        &::after {
            background: $c-button-disabled-bg;
        }
    }
}
</style>
